// ABOUTME: Style recommendation engine for outfit suggestions
// ABOUTME: Provides color matching, pattern rules, texture mixing, and outfit recommendations

import type { WardrobeItem } from '@/lib/store/wardrobeStore'
import type { WearLog } from '@/lib/store/wearLogStore'
import { useGameStore } from '@/lib/store/gameStore'
import { isColorInSeason } from '@/constants/colorPalettes'

/**
 * Color families for matching logic
 */
const COLOR_FAMILIES: Record<string, string[]> = {
  neutral: ['black', 'white', 'gray', 'grey', 'beige', 'cream', 'tan', 'brown', 'khaki', 'navy'],
  warm: ['red', 'orange', 'yellow', 'gold', 'rust', 'burgundy', 'maroon', 'coral', 'peach'],
  cool: ['blue', 'green', 'purple', 'teal', 'cyan', 'indigo', 'violet', 'turquoise'],
  earth: ['brown', 'tan', 'olive', 'khaki', 'beige', 'rust', 'terracotta'],
}

/**
 * Pattern types for outfit coordination
 */
const PATTERN_TYPES = {
  solid: ['solid', 'plain'],
  stripes: ['stripe', 'striped', 'pinstripe'],
  checks: ['check', 'checked', 'plaid', 'gingham', 'tartan', 'windowpane'],
  dots: ['dot', 'polka', 'spotted'],
  geometric: ['geometric', 'abstract', 'print'],
  floral: ['floral', 'flower', 'botanical'],
  textured: ['herringbone', 'tweed', 'houndstooth', 'birdseye'],
} as const

/**
 * Texture types for fabric mixing
 */
const TEXTURE_TYPES = {
  smooth: ['silk', 'satin', 'cotton', 'poplin', 'broadcloth'],
  matte: ['cotton', 'linen', 'flannel', 'chambray'],
  textured: ['tweed', 'corduroy', 'velvet', 'wool', 'knit', 'cable'],
  leather: ['leather', 'suede', 'nubuck'],
} as const

/**
 * Season/weather categories
 */
type WeatherSeason = 'spring' | 'summer' | 'fall' | 'winter'

const SEASON_FABRICS: Record<WeatherSeason, string[]> = {
  spring: ['cotton', 'linen', 'lightweight', 'chambray', 'poplin'],
  summer: ['linen', 'cotton', 'seersucker', 'lightweight', 'breathable'],
  fall: ['wool', 'flannel', 'tweed', 'corduroy', 'denim'],
  winter: ['wool', 'cashmere', 'flannel', 'tweed', 'heavy', 'fleece'],
}

const SEASON_COLORS: Record<WeatherSeason, string[]> = {
  spring: ['pastel', 'light', 'pink', 'mint', 'lavender', 'peach', 'sky blue'],
  summer: ['white', 'light', 'khaki', 'navy', 'coral', 'turquoise', 'bright'],
  fall: ['burgundy', 'rust', 'olive', 'forest', 'mustard', 'camel', 'brown', 'orange'],
  winter: ['navy', 'charcoal', 'black', 'burgundy', 'forest', 'deep', 'rich'],
}

/**
 * Occasion requirements
 */
interface OccasionRequirements {
  minFormality: number
  maxFormality: number
  preferredColors: string[]
  avoidPatterns: string[]
  tips: string[]
}

const OCCASION_RULES: Record<string, OccasionRequirements> = {
  business: {
    minFormality: 4,
    maxFormality: 5,
    preferredColors: ['navy', 'charcoal', 'gray', 'black', 'white', 'light blue'],
    avoidPatterns: ['floral', 'bold prints', 'graphic'],
    tips: ['Stick to conservative patterns', 'Match belt to shoes'],
  },
  formal: {
    minFormality: 5,
    maxFormality: 5,
    preferredColors: ['black', 'navy', 'charcoal', 'white'],
    avoidPatterns: ['bold patterns', 'bright colors'],
    tips: ['Classic colors only', 'Minimal accessories'],
  },
  'smart casual': {
    minFormality: 3,
    maxFormality: 4,
    preferredColors: ['navy', 'gray', 'white', 'blue', 'burgundy'],
    avoidPatterns: [],
    tips: ['Mix formal and casual pieces', 'Chinos work great here'],
  },
  casual: {
    minFormality: 1,
    maxFormality: 3,
    preferredColors: [],
    avoidPatterns: [],
    tips: ['Express your personal style', 'Comfort is key'],
  },
  date: {
    minFormality: 2,
    maxFormality: 4,
    preferredColors: ['navy', 'burgundy', 'white', 'black'],
    avoidPatterns: ['loud patterns'],
    tips: ['Look put-together but not overdressed', 'Dark colors are flattering'],
  },
  weekend: {
    minFormality: 1,
    maxFormality: 3,
    preferredColors: [],
    avoidPatterns: [],
    tips: ['Relax and be comfortable', 'Layer for versatility'],
  },
}

/**
 * Formality levels for different item categories
 */
const FORMALITY_LEVELS: Record<string, number> = {
  // Higher numbers = more formal
  suit: 5,
  blazer: 4,
  'dress shirt': 4,
  'oxford shirt': 3,
  'polo shirt': 2,
  't-shirt': 1,
  'dress pants': 4,
  chinos: 3,
  jeans: 2,
  shorts: 1,
  'dress shoes': 5,
  loafers: 3,
  sneakers: 1,
  boots: 2,
}

/**
 * Detect pattern type from item name/tags
 */
export function getPatternType(item: WardrobeItem): string {
  const searchText = `${item.name} ${item.tags.join(' ')}`.toLowerCase()

  for (const [patternType, patterns] of Object.entries(PATTERN_TYPES)) {
    if (patterns.some((p) => searchText.includes(p))) {
      return patternType
    }
  }

  return 'solid' // Default to solid
}

/**
 * Detect texture type from item name/tags
 */
export function getTextureType(item: WardrobeItem): string {
  const searchText = `${item.name} ${item.tags.join(' ')}`.toLowerCase()

  for (const [textureType, textures] of Object.entries(TEXTURE_TYPES)) {
    if (textures.some((t) => searchText.includes(t))) {
      return textureType
    }
  }

  return 'matte' // Default to matte cotton-like
}

/**
 * Check if two patterns work well together
 * Rule: Avoid two bold patterns; one solid + one pattern is ideal
 */
export function patternsMatch(item1: WardrobeItem, item2: WardrobeItem): { match: boolean; score: number; tip?: string } {
  const pattern1 = getPatternType(item1)
  const pattern2 = getPatternType(item2)

  // Solid with anything is always safe
  if (pattern1 === 'solid' || pattern2 === 'solid') {
    return { match: true, score: 100 }
  }

  // Two solids are perfect
  if (pattern1 === 'solid' && pattern2 === 'solid') {
    return { match: true, score: 95, tip: 'Add a patterned accessory for interest' }
  }

  // Same pattern family (e.g., stripes with stripes) - only works if different scales
  if (pattern1 === pattern2) {
    return { match: false, score: 40, tip: 'Avoid matching patterns; vary scale or choose one solid' }
  }

  // Stripes with checks can work
  if ((pattern1 === 'stripes' && pattern2 === 'checks') || (pattern1 === 'checks' && pattern2 === 'stripes')) {
    return { match: true, score: 70, tip: 'Ensure patterns differ in scale for best results' }
  }

  // Textured patterns (herringbone, tweed) pair well with solids but not with other patterns
  if (pattern1 === 'textured' || pattern2 === 'textured') {
    const other = pattern1 === 'textured' ? pattern2 : pattern1
    if (other === 'solid') {
      return { match: true, score: 90 }
    }
    return { match: false, score: 50, tip: 'Textured fabrics work best with solid companions' }
  }

  // Other pattern combinations - generally avoid
  return { match: false, score: 45, tip: 'Multiple patterns can clash; consider one solid piece' }
}

/**
 * Check if textures work well together
 * Rule: Mix textures for visual interest, but keep formality consistent
 */
export function texturesMatch(item1: WardrobeItem, item2: WardrobeItem): { match: boolean; score: number } {
  const texture1 = getTextureType(item1)
  const texture2 = getTextureType(item2)

  // Same texture is fine
  if (texture1 === texture2) {
    return { match: true, score: 80 }
  }

  // Smooth with matte - classic combination
  if ((texture1 === 'smooth' && texture2 === 'matte') || (texture1 === 'matte' && texture2 === 'smooth')) {
    return { match: true, score: 95 }
  }

  // Textured with smooth/matte - creates interest
  if (texture1 === 'textured' || texture2 === 'textured') {
    return { match: true, score: 85 }
  }

  // Leather with most things works
  if (texture1 === 'leather' || texture2 === 'leather') {
    return { match: true, score: 90 }
  }

  return { match: true, score: 75 }
}

/**
 * Determine current weather season based on month
 */
export function getCurrentWeatherSeason(): WeatherSeason {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

/**
 * Check if item is appropriate for current weather season
 */
export function isSeasonAppropriate(item: WardrobeItem, season?: WeatherSeason): { appropriate: boolean; score: number; tip?: string } {
  const currentSeason = season || getCurrentWeatherSeason()
  const searchText = `${item.name} ${item.tags.join(' ')}`.toLowerCase()
  const itemColor = item.color.toLowerCase()

  // Check fabric appropriateness
  const seasonFabrics = SEASON_FABRICS[currentSeason]
  const hasSuitableFabric = seasonFabrics.some((f) => searchText.includes(f))

  // Check color appropriateness
  const seasonColors = SEASON_COLORS[currentSeason]
  const hasSuitableColor = seasonColors.some((c) => itemColor.includes(c))

  // Universal items (black, navy, gray) always work
  const universalColors = ['black', 'navy', 'gray', 'grey', 'white']
  const isUniversal = universalColors.some((c) => itemColor.includes(c))

  if (hasSuitableFabric && (hasSuitableColor || isUniversal)) {
    return { appropriate: true, score: 100 }
  }

  if (hasSuitableFabric || hasSuitableColor || isUniversal) {
    return { appropriate: true, score: 80 }
  }

  // Check for season-inappropriate combos
  if (currentSeason === 'summer' && searchText.includes('wool')) {
    return { appropriate: false, score: 30, tip: 'Wool may be too warm for summer' }
  }
  if (currentSeason === 'winter' && searchText.includes('linen')) {
    return { appropriate: false, score: 40, tip: 'Linen is typically a warm-weather fabric' }
  }

  return { appropriate: true, score: 70 } // Default to acceptable
}

/**
 * Get occasion-specific requirements
 */
export function getOccasionRequirements(occasion: string): OccasionRequirements | null {
  const occasionLower = occasion.toLowerCase()
  return OCCASION_RULES[occasionLower] || null
}

/**
 * Check if outfit meets occasion requirements
 */
export function meetsOccasionRequirements(items: WardrobeItem[], occasion: string): { meets: boolean; score: number; tips: string[] } {
  const requirements = getOccasionRequirements(occasion)
  if (!requirements) {
    return { meets: true, score: 80, tips: [] }
  }

  const tips: string[] = []
  let score = 100

  // Check formality
  const avgFormality = items.reduce((sum, item) => sum + getFormalityLevel(item), 0) / items.length
  if (avgFormality < requirements.minFormality) {
    score -= 30
    tips.push('This outfit may be too casual for the occasion')
  } else if (avgFormality > requirements.maxFormality) {
    score -= 15
    tips.push('This outfit may be overdressed for the occasion')
  }

  // Check preferred colors
  if (requirements.preferredColors.length > 0) {
    const hasPreferredColor = items.some((item) =>
      requirements.preferredColors.some((c) => item.color.toLowerCase().includes(c))
    )
    if (!hasPreferredColor) {
      score -= 10
      tips.push(`Consider ${requirements.preferredColors.slice(0, 3).join(', ')} for this occasion`)
    }
  }

  // Check avoided patterns
  if (requirements.avoidPatterns.length > 0) {
    const hasAvoidedPattern = items.some((item) => {
      const pattern = getPatternType(item)
      return requirements.avoidPatterns.some((p) => pattern.includes(p) || item.name.toLowerCase().includes(p))
    })
    if (hasAvoidedPattern) {
      score -= 15
      tips.push('Consider simpler patterns for this occasion')
    }
  }

  // Add general occasion tips
  tips.push(...requirements.tips)

  return { meets: score >= 70, score: Math.max(0, score), tips }
}

/**
 * Determine color family of an item
 */
export function getColorFamily(color: string): string {
  const lowerColor = color.toLowerCase()

  for (const [family, colors] of Object.entries(COLOR_FAMILIES)) {
    if (colors.some((c) => lowerColor.includes(c))) {
      return family
    }
  }

  return 'neutral' // Default to neutral
}

/**
 * Check if two colors match well together
 */
export function colorsMatch(color1: string, color2: string): boolean {
  const family1 = getColorFamily(color1)
  const family2 = getColorFamily(color2)

  // Neutrals go with everything
  if (family1 === 'neutral' || family2 === 'neutral') {
    return true
  }

  // Same family colors usually match
  if (family1 === family2) {
    return true
  }

  // Warm and cool can complement each other in moderation
  // For MVP, we'll allow mixing but with lower score
  return true
}

/**
 * Check if item color is in user's seasonal palette
 */
export function isItemInUserSeason(item: WardrobeItem): boolean {
  const gameState = useGameStore.getState()
  const season = gameState.colorProfile.season

  if (!season) {
    return true // No penalty if no color profile
  }

  return isColorInSeason(item.color, season)
}

/**
 * Calculate color match score (0-100)
 * Considers user's seasonal palette if available
 */
export function getColorMatchScore(color1: string, color2: string): number {
  const family1 = getColorFamily(color1)
  const family2 = getColorFamily(color2)

  let baseScore = 0

  if (family1 === 'neutral' || family2 === 'neutral') {
    baseScore = 95 // Neutrals are nearly perfect with everything
  } else if (family1 === family2) {
    baseScore = 85 // Same family is very good
  } else {
    baseScore = 60 // Different families can work but with reduced score
  }

  // Bonus if colors are in user's seasonal palette
  const gameState = useGameStore.getState()
  const season = gameState.colorProfile.season

  if (season) {
    const color1InSeason = isColorInSeason(color1, season)
    const color2InSeason = isColorInSeason(color2, season)

    if (color1InSeason && color2InSeason) {
      baseScore = Math.min(100, baseScore + 10) // Bonus for both in season
    } else if (!color1InSeason && !color2InSeason) {
      baseScore = Math.max(0, baseScore - 10) // Penalty for neither in season
    }
  }

  return baseScore
}

/**
 * Estimate formality level of an item
 */
export function getFormalityLevel(item: WardrobeItem): number {
  const name = item.name.toLowerCase()
  const tags = item.tags.map((t) => t.toLowerCase())

  // Check tags first
  if (tags.includes('formal') || tags.includes('business')) return 5
  if (tags.includes('smart casual')) return 3
  if (tags.includes('casual')) return 2

  // Check name against known items
  for (const [itemType, level] of Object.entries(FORMALITY_LEVELS)) {
    if (name.includes(itemType)) {
      return level
    }
  }

  // Default based on category
  switch (item.category) {
    case 'outerwear':
      return 3
    case 'accessories':
      return 2
    default:
      return 3 // Moderate default
  }
}

/**
 * Check if outfit formality levels are compatible
 */
export function formalityMatch(item1: WardrobeItem, item2: WardrobeItem): boolean {
  const level1 = getFormalityLevel(item1)
  const level2 = getFormalityLevel(item2)

  // Allow 2 levels of difference
  return Math.abs(level1 - level2) <= 2
}

/**
 * Calculate outfit compatibility score (0-100)
 * Now includes pattern, texture, and season considerations
 */
export function calculateOutfitScore(items: WardrobeItem[], options?: { occasion?: string; season?: WeatherSeason }): number {
  if (items.length === 0) return 0
  if (items.length === 1) return 100

  let totalScore = 0
  let comparisons = 0
  const patternTips: string[] = []

  // Check all pairs of items
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const item1 = items[i]
      const item2 = items[j]

      // Color matching score (weight: 30%)
      const colorScore = getColorMatchScore(item1.color, item2.color)

      // Formality matching (weight: 25%)
      const formalityOk = formalityMatch(item1, item2)
      const formalityScore = formalityOk ? 100 : 50

      // Pattern matching (weight: 25%)
      const patternResult = patternsMatch(item1, item2)
      if (patternResult.tip) patternTips.push(patternResult.tip)

      // Texture matching (weight: 20%)
      const textureResult = texturesMatch(item1, item2)

      // Weighted average
      const pairScore =
        colorScore * 0.3 +
        formalityScore * 0.25 +
        patternResult.score * 0.25 +
        textureResult.score * 0.2

      totalScore += pairScore
      comparisons++
    }
  }

  let baseScore = comparisons > 0 ? Math.round(totalScore / comparisons) : 100

  // Apply season bonus/penalty
  const currentSeason = options?.season || getCurrentWeatherSeason()
  const seasonScores = items.map((item) => isSeasonAppropriate(item, currentSeason))
  const avgSeasonScore = seasonScores.reduce((sum, s) => sum + s.score, 0) / seasonScores.length
  // Adjust base score slightly based on season appropriateness
  baseScore = Math.round(baseScore * 0.85 + avgSeasonScore * 0.15)

  // Apply occasion bonus/penalty if specified
  if (options?.occasion) {
    const occasionResult = meetsOccasionRequirements(items, options.occasion)
    // Blend in occasion score
    baseScore = Math.round(baseScore * 0.8 + occasionResult.score * 0.2)
  }

  return Math.min(100, Math.max(0, baseScore))
}

/**
 * Recommend items that go well with a given item
 * Includes pattern, texture, and season-appropriate scoring
 */
export function recommendMatchingItems(
  baseItem: WardrobeItem,
  availableItems: WardrobeItem[],
  maxResults: number = 5
): Array<{ item: WardrobeItem; score: number; reason: string; tips?: string[] }> {
  const recommendations: Array<{ item: WardrobeItem; score: number; reason: string; tips?: string[] }> = []

  // Don't recommend items from the same category
  const candidateItems = availableItems.filter((item) => item.category !== baseItem.category)

  for (const item of candidateItems) {
    const tips: string[] = []

    // Color matching (weight: 30%)
    const colorScore = getColorMatchScore(baseItem.color, item.color)

    // Formality matching (weight: 20%)
    const formalityOk = formalityMatch(baseItem, item)
    const formalityScore = formalityOk ? 100 : 50

    // Pattern matching (weight: 25%)
    const patternResult = patternsMatch(baseItem, item)
    if (patternResult.tip) tips.push(patternResult.tip)

    // Texture matching (weight: 15%)
    const textureResult = texturesMatch(baseItem, item)

    // Season appropriateness (weight: 10%)
    const seasonResult = isSeasonAppropriate(item)
    if (seasonResult.tip) tips.push(seasonResult.tip)

    // Calculate weighted overall score
    const overallScore = Math.round(
      colorScore * 0.3 +
      formalityScore * 0.2 +
      patternResult.score * 0.25 +
      textureResult.score * 0.15 +
      seasonResult.score * 0.1
    )

    // Build reason string
    let reason = ''
    if (colorScore > 90) {
      reason = 'Perfect color match'
    } else if (colorScore > 80) {
      reason = 'Great color combination'
    } else if (colorScore > 70) {
      reason = 'Good color pairing'
    } else {
      reason = 'Complementary colors'
    }

    // Add pattern info
    const basePattern = getPatternType(baseItem)
    const itemPattern = getPatternType(item)
    if (basePattern !== 'solid' && itemPattern === 'solid') {
      reason += ' (solid pairs well with pattern)'
    } else if (basePattern === 'solid' && itemPattern !== 'solid') {
      reason += ' (pattern adds visual interest)'
    }

    if (!formalityOk) {
      reason += ' (formality mismatch)'
    }

    // Add note if item is in user's seasonal palette
    const itemInSeason = isItemInUserSeason(item)
    const baseInSeason = isItemInUserSeason(baseItem)

    if (itemInSeason && baseInSeason) {
      reason += ' ✓ In your season'
    }

    // Season weather note
    if (seasonResult.appropriate && seasonResult.score >= 90) {
      reason += ' ✓ Season-appropriate'
    }

    recommendations.push({ item, score: overallScore, reason, tips: tips.length > 0 ? tips : undefined })
  }

  // Sort by score and return top results
  return recommendations.sort((a, b) => b.score - a.score).slice(0, maxResults)
}

/**
 * Generate complete outfit suggestions from wardrobe
 */
export function generateOutfitSuggestions(
  items: WardrobeItem[],
  occasion?: string,
  maxSuggestions: number = 3
): Array<{ items: WardrobeItem[]; score: number; description: string }> {
  if (items.length < 3) {
    return [] // Need at least 3 items for a complete outfit
  }

  const suggestions: Array<{ items: WardrobeItem[]; score: number; description: string }> = []

  // Get items by category
  const tops = items.filter((i) => i.category === 'tops')
  const bottoms = items.filter((i) => i.category === 'bottoms')
  const shoes = items.filter((i) => i.category === 'shoes')

  // Generate combinations
  for (const top of tops.slice(0, 5)) {
    // Limit iterations
    for (const bottom of bottoms.slice(0, 5)) {
      for (const shoe of shoes.slice(0, 3)) {
        const outfitItems = [top, bottom, shoe]
        const score = calculateOutfitScore(outfitItems, { occasion })

        // Only include good matches
        if (score >= 60) {
          const formalityLevel = Math.round(
            outfitItems.reduce((sum, item) => sum + getFormalityLevel(item), 0) / outfitItems.length
          )

          let description = ''
          if (formalityLevel >= 4) {
            description = 'Formal outfit for business and special occasions'
          } else if (formalityLevel >= 3) {
            description = 'Smart casual look for versatile wear'
          } else {
            description = 'Casual everyday outfit'
          }

          suggestions.push({ items: outfitItems, score, description })
        }
      }
    }
  }

  // Filter by occasion if specified
  let filteredSuggestions = suggestions
  if (occasion) {
    const requirements = getOccasionRequirements(occasion)
    if (requirements) {
      // Use occasion requirements to filter suggestions
      filteredSuggestions = suggestions.filter((s) => {
        const occasionResult = meetsOccasionRequirements(s.items, occasion)
        return occasionResult.meets
      })
    } else {
      // Fallback to formality-based filtering for unknown occasions
      const occasionLower = occasion.toLowerCase()
      if (occasionLower.includes('formal') || occasionLower.includes('business')) {
        filteredSuggestions = suggestions.filter((s) => {
          const avgFormality =
            s.items.reduce((sum, item) => sum + getFormalityLevel(item), 0) / s.items.length
          return avgFormality >= 3.5
        })
      } else if (occasionLower.includes('casual')) {
        filteredSuggestions = suggestions.filter((s) => {
          const avgFormality =
            s.items.reduce((sum, item) => sum + getFormalityLevel(item), 0) / s.items.length
          return avgFormality <= 3
        })
      }
    }
  }

  // Sort by score and return top suggestions
  return filteredSuggestions.sort((a, b) => b.score - a.score).slice(0, maxSuggestions)
}

/**
 * Calculate recency multiplier for an item (0.0-1.0)
 * Items worn recently get penalized, neglected items get boosted
 */
export function getRecencyScore(item: WardrobeItem, logs: WearLog[]): number {
  let lastWornDate: string | null = null
  for (const log of logs) {
    if (log.itemIds.includes(item.id)) {
      if (!lastWornDate || log.date > lastWornDate) lastWornDate = log.date
    }
  }

  if (!lastWornDate) return 1.0

  const now = new Date()
  const lastWorn = new Date(lastWornDate)
  const daysSince = Math.floor((now.getTime() - lastWorn.getTime()) / (1000 * 60 * 60 * 24))

  if (daysSince <= 1) return 0.3
  if (daysSince <= 3) return 0.5
  if (daysSince <= 7) return 0.7
  if (daysSince <= 14) return 0.9
  return 1.0
}

/**
 * Generate daily outfit suggestions weighted by recency
 */
export function generateDailySuggestions(
  items: WardrobeItem[],
  logs: WearLog[],
  limit: number = 3
): Array<{ items: WardrobeItem[]; score: number; description: string }> {
  const baseSuggestions = generateOutfitSuggestions(items, undefined, limit * 3)

  const scored = baseSuggestions.map((suggestion) => {
    const recencyBoost =
      suggestion.items.reduce((sum, item) => sum + getRecencyScore(item, logs), 0) /
      suggestion.items.length

    return {
      ...suggestion,
      score: Math.round(suggestion.score * (0.6 + recencyBoost * 0.4)),
    }
  })

  return scored.sort((a, b) => b.score - a.score).slice(0, limit)
}
